/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import {
  ConfirmDialog,
  ErrorAlert,
  LocationCard,
  PageBreadcrumb,
  PaginationControl,
} from '@repo/shared-client/components/blocks'
import {
  Alert,
  AlertDescription,
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Rating,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { FeatureFlag, getFeatureFlag, UserRole } from '@repo/shared-common'
import { createFileRoute, Link, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import SFReviewStatus from '@web/src/components/primatives/sf-review-status/sf-review-status'
import SFRichTextViewer from '@web/src/components/primatives/sf-rich-text-viewer/sf-rich-text-viewer'
import ChatMessageFloater from '@web/src/features/chat/chat-message-floater/chat-message-floater'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import MediaUploaderDialog from '@web/src/features/media/media-uploader-dialog/media-uploader-dialog'
import SimpleMediaImageViewer from '@web/src/features/media/simple-media-image-viewer/simple-media-image-viewer'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import Reviews from '@web/src/features/review/reviews/reviews'
import { useVendorMarketCategoryOptions } from '@web/src/features/vendor-market/use-vendor-market-category-options'
import VmDelete from '@web/src/features/vendor-market/vm-delete/vm-delete'
import VmItemCard from '@web/src/features/vendor-market/vm-item-card/vm-item-card'
import VmTransferModal from '@web/src/features/vendor-market/vm-transfer-modal/vm-transfer-modal'
import { useAuth } from '@web/src/hooks/use-auth'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import {
  AttachedMediaInfo,
  ProfileInfo,
  ProfileMediaInfo,
  VendorMarketInfo,
  VendorMarketItemInfo,
} from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { EllipsisIcon, FilmIcon, LinkIcon, MailIcon, PlusIcon, SmartphoneIcon } from 'lucide-react'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  DeleteAttachedMediaMutation,
  DeleteAttachedMediaMutationVariables,
  VendorMarketAdminApproveMutation,
  VendorMarketAdminApproveMutationVariables,
  VendorMarketDetailQuery,
  VendorMarketDetailQueryVariables,
} from 'types/graphql'
import { z } from 'zod'
import { vendorMarketPageHeadFromLoaderData } from './$vendorMarketIdOrSlug.seo'

/** Viewer cap for non-supporter listings (permission id: vendor-market:image-limit); same value as member role in ROLE_FEATURE_FLAGS. */
const PUBLIC_VIEWER_VM_IMAGE_LIMIT = getFeatureFlag(UserRole.MEMBER, FeatureFlag.VENDOR_MARKET_IMAGE_LIMIT)
const PUBLIC_VIEWER_VM_ITEM_LIMIT = getFeatureFlag(UserRole.MEMBER, FeatureFlag.VENDOR_MARKET_ITEM_LIMIT)

const galleryThumbCardClass =
  'cursor-pointer border border-border bg-muted w-full transition-colors hover:border-primary hover:bg-muted/80'

type VendorMarketDetail = NonNullable<VendorMarketDetailQuery['vendorMarket']>
type VendorMarketAttachedMedia = NonNullable<VendorMarketDetail['attachedMedia']>[number]

const VENDOR_MARKET_DETAIL_QUERY: TypedDocumentNode<VendorMarketDetailQuery, VendorMarketDetailQueryVariables> = gql`
  query VendorMarketDetail($vendorMarketIdOrSlug: String!, $itemsPage: Int) {
    vendorMarket(vendorMarketIdOrSlug: $vendorMarketIdOrSlug) {
      ...VendorMarketInfo
      owner {
        ...ProfileInfo
      }
      attachedMedia {
        ...AttachedMediaInfo
      }
    }
    vendorMarketItemsPage(vendorMarketIdOrSlug: $vendorMarketIdOrSlug, page: $itemsPage) {
      items {
        ...VendorMarketItemInfo
        firstMedia {
          ...AttachedMediaInfo
        }
      }
      count
      perPage
    }
  }
  ${VendorMarketInfo}
  ${VendorMarketItemInfo}
  ${AttachedMediaInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

const DELETE_IMAGE: TypedDocumentNode<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables> = gql`
  mutation DeleteAttachedMedia($id: String!) {
    deleteAttachedMedia(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_UP = gql`
  mutation MoveAttachedMediaUp($id: String!) {
    moveAttachedMediaUp(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_DOWN = gql`
  mutation MoveAttachedMediaDown($id: String!) {
    moveAttachedMediaDown(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_TO_TOP = gql`
  mutation MoveAttachedMediaToTop($id: String!) {
    moveAttachedMediaToTop(id: $id) {
      id
    }
  }
`

const MOVE_MEDIA_TO_BOTTOM = gql`
  mutation MoveAttachedMediaToBottom($id: String!) {
    moveAttachedMediaToBottom(id: $id) {
      id
    }
  }
`

const APPROVE_VM: TypedDocumentNode<VendorMarketAdminApproveMutation, VendorMarketAdminApproveMutationVariables> = gql`
  mutation VendorMarketAdminApprove($vendorMarketIdOrSlug: String!) {
    vendorMarketAdminApprove(vendorMarketIdOrSlug: $vendorMarketIdOrSlug) {
      id
      reviewStatus
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/$vendorMarketIdOrSlug')({
  component: VendorMarketDetailPage,
  validateSearch: z.object({
    itemsPage: z.coerce.number().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        itemsPage: 1,
      }),
    ],
  },
  head: ({ loaderData }) => vendorMarketPageHeadFromLoaderData(loaderData),
  loaderDeps: ({ search }) => {
    const s = search as { itemsPage?: number }
    return { itemsPage: s.itemsPage ?? 1 }
  },
  loader: async ({ params, context, deps }) => {
    const { itemsPage } = deps as { itemsPage: number }
    const { queryData: vendorData, queryRef: vendorQueryRef } = await queryAndPreload(
      context,
      VENDOR_MARKET_DETAIL_QUERY,
      {
        variables: { vendorMarketIdOrSlug: params.vendorMarketIdOrSlug, itemsPage },
      }
    )
    return {
      queryRef: vendorQueryRef,
      vendorMarketDetailQueryData: vendorData,
      vendorMarket: vendorData?.vendorMarket,
    }
  },
})

function VendorMarketDetailPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const { data, error } = useReadQuery(loaderData.queryRef as QueryRef<VendorMarketDetailQuery>)
  const resolvedVendorDetailQuery = data ?? loaderData.vendorMarketDetailQueryData
  const vm = resolvedVendorDetailQuery?.vendorMarket ?? loaderData.vendorMarket
  const itemsPageData =
    resolvedVendorDetailQuery?.vendorMarketItemsPage ?? loaderData.vendorMarketDetailQueryData?.vendorMarketItemsPage

  if (error) return <ErrorAlert error={error} />
  if (!vm) return <ErrorAlert error={new Error(t('Vendor not found'))} />

  return <VendorMarketDetailMain itemsPageData={itemsPageData} vm={vm} />
}

function VendorMarketDetailMain({
  itemsPageData,
  vm,
}: {
  itemsPageData: VendorMarketDetailQuery['vendorMarketItemsPage'] | null | undefined
  vm: VendorMarketDetail
}) {
  const { t } = useTranslation()
  const { currentUser, hasPermission } = useAuth()
  const { mapBoxToken } = useMapBox()
  const searchParams = useSearch({ strict: false })
  const itemsPageFromSearch = searchParams.itemsPage ? Number.parseInt(String(searchParams.itemsPage), 10) : 1
  const navigate = useNavigate()
  const client = useApolloClient()
  const { labelFor } = useVendorMarketCategoryOptions()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showReportDialog, setShowReportDialog] = useState(false)
  const [uploadImageModal, setUploadImageModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)

  const handleItemsPageChange = useCallback(
    (nextPage: number) => {
      navigate({ search: { ...searchParams, itemsPage: nextPage }, replace: true })
    },
    [navigate, searchParams]
  )

  const handleRefetch = async () => {
    await client.refetchQueries({ include: [VENDOR_MARKET_DETAIL_QUERY] })
  }

  const [approveVM] = useMutation(APPROVE_VM, {
    onCompleted: () => {
      toast.success(t('Vendor Approved'))
      handleRefetch()
    },
    onError: (err) => toast.error(err.message),
  })

  const locationExact = !vm.serviceRange
  const canViewMap = hasPermission('location:view-map') && Boolean(mapBoxToken)
  const distanceRaw = vm.distance
  const distanceKm = distanceRaw != null && Number.isFinite(distanceRaw) ? Math.round(distanceRaw) : 0
  const locationDisplayText = locationExact ? (vm.location ?? '') : `Near ${vm.location ?? ''}`
  const latitude = vm.locationLat ?? 0
  const longitude = vm.locationLong ?? 0
  const mapRangeKm = locationExact ? undefined : vm.serviceRange

  let avgRating = 0
  if (vm.reviewCount > 0) {
    avgRating = Math.min(Math.max(0, vm.reviewSum / vm.reviewCount), 5.0)
  }

  const vendorRouteSegment = vm.slug ?? vm.id
  const mediaCount = vm.attachedMedia?.length ?? 0

  const isVendorMarketOwner = currentUser?.profile?.id === vm.ownerId
  const showNonSupporterMediaLimitNotice =
    (isVendorMarketOwner || hasPermission('vendor-market:read:all')) &&
    vm.owner &&
    !vm.owner.supporter &&
    (vm.attachedMedia?.length ?? 0) > PUBLIC_VIEWER_VM_IMAGE_LIMIT

  const showNonSupporterItemLimitNotice =
    (isVendorMarketOwner || hasPermission('vendor-market:read:all')) &&
    vm.owner &&
    !vm.owner.supporter &&
    (itemsPageData?.items?.length ?? 0) > PUBLIC_VIEWER_VM_ITEM_LIMIT

  return (
    <div className="mx-auto w-full max-w-4xl px-2 py-2 md:px-4">
      <div className="flex w-full flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t('Home'), to: '/' },
            { label: t('Vendor Market'), to: '/vendor-market' },
            { label: t('Discover Vendors'), to: '/vendor-market/list' },
            { label: vm.name },
          ]}
        />

        <div className="m-0 flex justify-center gap-0 self-center p-0">
          <SFReviewStatus reviewStatus={vm.reviewStatus} size="lg" />
        </div>

        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <h1 className="font-bold text-2xl">{vm.name}</h1>
            <div className="flex w-full items-center justify-between gap-2">
              <Badge className="shadow-xs" size="lg">
                {labelFor(vm.category)}
              </Badge>
              <div className="flex items-center gap-2">
                <Rating size="sm" value={avgRating} />
                <span className="pr-2 text-sm">({vm.reviewCount})</span>
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  aria-label={t('Vendor options')}
                  className="shrink-0"
                  size="icon"
                  type="button"
                  variant="default"
                />
              }
            >
              <EllipsisIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {vm.canEdit ? (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: '/vendor-market/$vendorMarketIdOrSlug/edit',
                      params: { vendorMarketIdOrSlug: vendorRouteSegment } as never,
                    })
                  }
                >
                  {t('Edit')}
                </DropdownMenuItem>
              ) : null}
              {vm.canAddItem ? (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: '/vendor-market/$vendorMarketIdOrSlug/item/create',
                      params: { vendorMarketIdOrSlug: vendorRouteSegment } as never,
                    })
                  }
                >
                  {t('Add Item')}
                </DropdownMenuItem>
              ) : null}
              {vm.canApprove && vm.reviewStatus !== 'ACCEPTED' ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => approveVM({ variables: { vendorMarketIdOrSlug: vendorRouteSegment } })}
                  >
                    {t('Approve')}
                  </DropdownMenuItem>
                </>
              ) : null}
              {vm.canTransfer ? (
                <DropdownMenuItem onClick={() => setShowTransferModal(true)}>
                  {t('Transfer Maintainer')}
                </DropdownMenuItem>
              ) : null}
              {vm.canReport ? (
                <DropdownMenuItem onClick={() => setShowReportDialog(true)}>{t('Report')}</DropdownMenuItem>
              ) : null}
              {vm.canDelete ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowDeleteDialog(true)} variant="destructive">
                    {t('Delete')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {vm.tagLine ? <p className="w-full text-lg italic">{vm.tagLine}</p> : null}

        {vm.owner ? (
          <>
            <Separator className="w-full" />
            <SFProfileBanner
              lowerTitle={<span className="line-clamp-1 text-sm">{t('Vendor')}</span>}
              profile={vm.owner}
              size="lg"
            />
            <Separator className="w-full" />
          </>
        ) : null}

        {vm.phone || vm.email || (vm.webLinks && vm.webLinks.length > 0) ? (
          <div className="flex w-full flex-col items-start gap-2 md:hidden">
            {vm.phone ? (
              <div className="flex items-center gap-2">
                <SmartphoneIcon className="size-4 shrink-0 text-muted-foreground" />
                <span>{vm.phone}</span>
              </div>
            ) : null}
            {vm.email ? (
              <div className="flex items-center gap-2">
                <MailIcon className="size-4 shrink-0 text-muted-foreground" />
                <a className="text-foreground" href={`mailto:${vm.email}`} rel="noopener noreferrer">
                  {vm.email}
                </a>
              </div>
            ) : null}
            {vm.webLinks?.map((link, i) => (
              <div className="flex items-center gap-2" key={i}>
                <LinkIcon className="size-4 shrink-0 text-muted-foreground" />
                <a className="text-primary" href={link} rel="noopener noreferrer" target="_blank">
                  {link}
                </a>
              </div>
            ))}
          </div>
        ) : null}

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]">
          <div className="flex w-full min-w-0 flex-col items-start gap-4">
            <div className="flex w-full flex-col gap-3">
              {vm.attachedMedia && vm.attachedMedia.length > 0 ? (
                <div className="flex w-full flex-col items-stretch gap-2 md:flex-row">
                  {vm.attachedMedia.map((attachedMedia, idx) => {
                    if (idx > 1) {
                      return null
                    }

                    const showHeroPairOnMd = vm.attachedMedia.length > 1

                    return (
                      <Card
                        className={cn(
                          galleryThumbCardClass,
                          'overflow-hidden p-0',
                          showHeroPairOnMd && 'md:w-1/2 md:min-w-0',
                          idx > 0 && 'hidden md:block'
                        )}
                        key={attachedMedia.id}
                        onClick={() => {
                          setImageIndex(idx)
                          setViewModal(true)
                        }}
                      >
                        <AspectRatio ratio={16 / 9}>
                          <SFMediaImage
                            imageProps={{
                              alt:
                                idx > 0
                                  ? t('{{vendorName}} — secondary hero image', { vendorName: vm.name })
                                  : t('{{vendorName}} — primary hero image', { vendorName: vm.name }),
                            }}
                            media={attachedMedia}
                          />
                        </AspectRatio>
                      </Card>
                    )
                  })}
                </div>
              ) : null}

              <div className="flex w-full items-start justify-start gap-2 overflow-x-auto overflow-y-hidden">
                {vm.canUploadMedia ? (
                  <AspectRatio className="w-[125px] min-w-[125px]" ratio={1}>
                    <Card
                      className={cn(
                        galleryThumbCardClass,
                        'flex h-full flex-col items-center justify-center gap-2 p-2'
                      )}
                      onClick={() => setUploadImageModal(true)}
                    >
                      <FilmIcon className="size-8" />
                      <div className="flex flex-col text-center text-base">
                        <span>{t('Upload')}</span>
                        <span>{t('Media')}</span>
                      </div>
                    </Card>
                  </AspectRatio>
                ) : null}

                {vm.attachedMedia?.map((attachedMedia, idx) => (
                  <ImageCard
                    idx={idx}
                    key={attachedMedia.id}
                    media={attachedMedia}
                    onImageSelected={(selectedIdx) => {
                      setImageIndex(selectedIdx)
                      setViewModal(true)
                    }}
                    onRefetch={handleRefetch}
                    totalMedia={mediaCount}
                    vm={vm}
                  />
                ))}
              </div>

              {showNonSupporterMediaLimitNotice ? (
                <Alert variant="destructive">
                  <AlertDescription className="flex flex-col items-start gap-2">
                    <span>
                      {t(
                        'Only the first {{count}} media files are visible to visitors using a free account. Become a supporter to allow visitors to see all your media.',
                        { count: PUBLIC_VIEWER_VM_IMAGE_LIMIT }
                      )}
                    </span>
                    <Button
                      className="w-fit"
                      nativeButton={false}
                      render={<Link className="no-underline" to="/supporter" />}
                    >
                      {t('Become a Supporter')}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>

            {vm.description ? (
              <Card className="w-full">
                <CardHeader>
                  <CardTitle>{t('About')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <SFRichTextViewer content={vm.description} />
                </CardContent>
              </Card>
            ) : null}

            {itemsPageData && itemsPageData.count > 0 ? (
              <Card className="w-full">
                <CardHeader>
                  <div className="flex w-full items-center justify-between">
                    <CardTitle>{t('Items')}</CardTitle>
                    {vm.canAddItem ? (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              aria-label={t('Add Item')}
                              onClick={() =>
                                navigate({
                                  to: '/vendor-market/$vendorMarketIdOrSlug/item/create',
                                  params: { vendorMarketIdOrSlug: vendorRouteSegment } as never,
                                })
                              }
                              size="icon"
                              type="button"
                            />
                          }
                        >
                          <PlusIcon className="size-4" />
                        </TooltipTrigger>
                        <TooltipContent>{t('Add Item')}</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 px-2">
                  {showNonSupporterItemLimitNotice ? (
                    <Alert variant="destructive">
                      <AlertDescription className="flex flex-col items-start gap-2">
                        <span>
                          {t(
                            'Only the first {{count}} items are visible to visitors using a free account. Become a supporter to allow visitors to see all your items.',
                            { count: PUBLIC_VIEWER_VM_ITEM_LIMIT }
                          )}
                        </span>
                        <Button className="w-fit" nativeButton={false} render={<Link to="/supporter" />}>
                          {t('Become a Supporter')}
                        </Button>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  <div className="grid grid-cols-2 gap-3">
                    {itemsPageData.items.map((item) => (
                      <VmItemCard item={item} key={item.id} vendorMarketId={vendorRouteSegment} />
                    ))}
                  </div>
                  <PaginationControl
                    count={itemsPageData.count}
                    onPageChange={(e) => handleItemsPageChange(e.page)}
                    page={itemsPageFromSearch}
                    pageSize={itemsPageData.perPage}
                  />
                </CardContent>
              </Card>
            ) : null}

            {itemsPageData && itemsPageData.count === 0 && vm.canAddItem ? (
              <Card className="w-full">
                <CardContent>
                  <div className="flex flex-col items-center gap-2">
                    <span>{t('No items yet')}</span>
                    <Button
                      onClick={() =>
                        navigate({
                          to: '/vendor-market/$vendorMarketIdOrSlug/item/create',
                          params: { vendorMarketIdOrSlug: vendorRouteSegment } as never,
                        })
                      }
                      type="button"
                    >
                      {t('Add Item')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>

          <div className="flex w-full flex-col gap-4">
            <LocationCard
              distanceKm={distanceKm}
              hideRangeTitle={locationExact}
              latitude={latitude}
              location={locationDisplayText}
              longitude={longitude}
              mapboxToken={mapBoxToken}
              rangeKm={mapRangeKm}
              rangeTitle={t('Service Area:')}
              showSupporterPlaceholder={!canViewMap}
            />
            {vm.phone || vm.email || (vm.webLinks && vm.webLinks.length > 0) ? (
              <Card className="hidden w-full md:block">
                <CardContent>
                  <div className="flex w-full flex-col items-start gap-4">
                    {vm.phone ? (
                      <div className="flex items-center gap-2">
                        <SmartphoneIcon className="size-4 shrink-0 text-muted-foreground" />
                        <span>{vm.phone}</span>
                      </div>
                    ) : null}
                    {vm.email ? (
                      <div className="flex items-center gap-2">
                        <MailIcon className="size-4 shrink-0 text-muted-foreground" />
                        <a className="text-foreground" href={`mailto:${vm.email}`} rel="noopener noreferrer">
                          {vm.email}
                        </a>
                      </div>
                    ) : null}
                    {vm.webLinks?.map((link, i) => (
                      <div className="flex items-center gap-2" key={i}>
                        <LinkIcon className="size-4 shrink-0 text-muted-foreground" />
                        <a className="text-primary" href={link} rel="noopener noreferrer" target="_blank">
                          {link}
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>

        <Reviews canReview={vm.canReview} vendorMarketIdOrSlug={vendorRouteSegment} vendorMarketOwnerId={vm?.ownerId} />
      </div>

      <MediaUploaderDialog
        onOpenChange={(e) => setUploadImageModal(e.open)}
        onUploadComplete={handleRefetch}
        open={uploadImageModal}
        vendorMarketId={vm.id}
      />

      <VmDelete
        onOpenChange={(e) => setShowDeleteDialog(e.open)}
        open={showDeleteDialog}
        vendorMarket={{ id: vm.id, name: vm.name }}
      />

      <VmTransferModal
        onComplete={handleRefetch}
        onOpenChange={(e) => setShowTransferModal(e.open)}
        open={showTransferModal}
        vendorMarketId={vm.id}
      />

      {showReportDialog ? (
        <ReportCreate
          onOpenChange={(e) => setShowReportDialog(e.open)}
          open={showReportDialog}
          targetId={vm.id}
          targetKind="VENDOR_MARKET"
        />
      ) : null}

      {!!vm.attachedMedia && vm.attachedMedia.length > 0 ? (
        <SimpleMediaImageViewer
          attachedMedia={true}
          imageIndex={imageIndex}
          mediaArray={vm.attachedMedia}
          onMediaChange={handleRefetch}
          onOpenChange={(e) => setViewModal(e.open)}
          open={viewModal}
          vendorMarketId={vm.id}
        />
      ) : null}

      {currentUser.profile?.id !== vm.ownerId ? <ChatMessageFloater profileId={vm.ownerId} /> : null}
    </div>
  )
}

type ImageCardProps = {
  idx: number
  media: VendorMarketAttachedMedia
  onImageSelected: (idx: number) => void
  onRefetch: () => void | Promise<void>
  vm: VendorMarketDetail
  totalMedia: number
}

function ImageCard({ onRefetch, vm, media, idx, onImageSelected, totalMedia }: ImageCardProps) {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [adminEditModal, setAdminEditModal] = useState(false)

  const [deleteImage] = useMutation(DELETE_IMAGE, {
    onCompleted: () => {
      toast.success(t('Image Deleted'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveUp] = useMutation(MOVE_MEDIA_UP, {
    onCompleted: () => {
      toast.success(t('Media moved up'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveDown] = useMutation(MOVE_MEDIA_DOWN, {
    onCompleted: () => {
      toast.success(t('Media moved down'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToTop] = useMutation(MOVE_MEDIA_TO_TOP, {
    onCompleted: () => {
      toast.success(t('Media moved to top'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [moveToBottom] = useMutation(MOVE_MEDIA_TO_BOTTOM, {
    onCompleted: () => {
      toast.success(t('Media moved to bottom'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleDeleteImage = () => {
    setDeleteModal(false)
    deleteImage({ variables: { id: media.id } })
  }

  return (
    <div className="relative shrink-0">
      <AspectRatio className="relative w-[125px] min-w-[125px]" ratio={1}>
        <Card className={cn(galleryThumbCardClass, 'h-full overflow-hidden p-0')} onClick={() => onImageSelected(idx)}>
          <SFMediaImage
            imageProps={{
              alt: t('{{vendorName}} — additional gallery image', { vendorName: vm.name }),
            }}
            media={media}
          />
        </Card>
      </AspectRatio>
      {vm.canDeleteMedia ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="absolute top-0.5 right-0.5 z-10"
            onClick={(e) => e.stopPropagation()}
            render={
              <Button
                aria-label={t('Media options')}
                className="rounded-full border-black p-0"
                size="icon-xs"
                type="button"
                variant="default"
              />
            }
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveToTop({ variables: { id: media.id } })}>
                {t('Move to Top')}
              </DropdownMenuItem>
            ) : null}
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveUp({ variables: { id: media.id } })}>
                {t('Move Up')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveDown({ variables: { id: media.id } })}>
                {t('Move Down')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveToBottom({ variables: { id: media.id } })}>
                {t('Move to Bottom')}
              </DropdownMenuItem>
            ) : null}

            <MediaAdminEditMenuItems mediaId={media.id} onOpen={() => setAdminEditModal(true)} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDeleteModal(true)} variant="destructive">
              {t('Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <ConfirmDialog
        onConfirm={handleDeleteImage}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this image?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <MediaAdminEditDialog
        isAttached
        mediaId={media.id}
        onMediaChange={onRefetch}
        onOpenChange={(e) => setAdminEditModal(e.open)}
        open={adminEditModal}
      />
    </div>
  )
}
